<template>
  <div>
    <v-breadcrumbs data-cy="breadcrumbs" :items="breadcrumbs" class="ml-n5" divider=">"> </v-breadcrumbs>
    <v-row>
      <v-col cols="6">
        <h1 class="font-weight-thin">Application Administration</h1>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="10">
        <p>
          The Application Administration module lets members
          <span class="font-weight-bold">indicate which applications impact their organization</span>. When a pack moves
          into user acceptance testing (UAT) the following automatically applies:
          <span>
            <ul>
              <li>Applications checked as “Impacted” default a release note’s testing status to “Not Started”.</li>
              <li>Applications not checked as “Impacted” default a release note’s testing status to “Not Impacted”.</li>
            </ul>
          </span>
        </p>
        <p>
          <span class="font-weight-bold">Note:</span> If you do not make any selections, the default testing status will
          be "Not Started."
        </p>
        <v-alert
          color="primary"
          data-test="alertMessage"
          dense
          outlined
          type="info"
          v-if="applicationAdminLastUpdatedDate === null"
        >
          No modifications have ever been made to the Application List for this organization. Acknowledging indicates
          that ALL applications impact your organization.
          <div>
            <v-btn @click="acknowledge" outlined color="primary" class="mt-3" data-cy="acknowledge-button"
              >acknowledge</v-btn
            >
          </div>
        </v-alert>
      </v-col>
    </v-row>
    <v-row>
      <v-col v-if="excludedApplicationListHasEdits && applicationList" class="ml-n3">
        <application-admin-change-history
          :adminDropdownList="applicationListForHistory"
          :auditHistory="filteredAuditLogList"
        ></application-admin-change-history>
      </v-col>
    </v-row>
    <v-app-bar class="mt-3" flat color="gray" dense>
      <v-toolbar-title class="text-h6 pl-0 font-weight-thin">
        Application List
      </v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn
        @click="enableEdit = true"
        color="primary"
        data-cy="applicationEditButton"
        elevation="0"
        small
        outlined
        v-if="!enableEdit"
      >
        <v-icon class="mr-2" small>fas fa-edit</v-icon>Edit</v-btn
      >
      <v-btn @click="cancel" class="mr-2 mt-2" data-cy="cancelButton" elevation="0" outlined small v-if="enableEdit"
        >Cancel</v-btn
      >
      <v-btn
        @click="setConfirmationDialog(true)"
        :disabled="isEqual(excludedApplicationList, excludedApplicationListUpdated)"
        class="mt-2"
        color="primary"
        data-cy="saveButton"
        elevation="0"
        outlined
        small
        v-if="enableEdit"
        >Save</v-btn
      >
    </v-app-bar>

    <v-divider class="mt-5"></v-divider>
    <v-col>
      <generic-loader caption="Fetching data..." topMargin="10" v-if="isLoadingDropdownList"></generic-loader>
      <v-card class="elevation-0 mt-5" v-else>
        <v-data-table
          data-cy="applicationAdmin"
          dense
          disable-pagination
          hide-default-footer
          :items="applicationListWithMemberChoiceSelected"
          :headers="headers"
          :search="search"
        >
          <template v-slot:top>
            <v-row>
              <v-col sm="12" md="6">
                <v-text-field
                  clearable
                  dense
                  data-cy="search"
                  label="Search"
                  prepend-inner-icon="search"
                  single-line
                  outlined
                  v-model="search"
                ></v-text-field>
              </v-col>
              <v-col v-if="excludedApplicationList">
                <v-checkbox
                  :label="`Show only applications that do not impact my organization`"
                  color="primary"
                  data-cy="application-checkbox"
                  dense
                  id="application-checkbox"
                  v-model="showOptedOutList"
                ></v-checkbox>
              </v-col>
            </v-row>
          </template>
          <template v-slot:item="{ item }">
            <tr>
              <template>
                <td>
                  <v-checkbox
                    :data-cy="`application-checkbox-${item.id}`"
                    :disabled="!enableEdit"
                    :id="`application-checkbox-${item.id}`"
                    :key="item.id"
                    small
                    v-model="item.memberSelection"
                    :true-value="false"
                    :false-value="true"
                  >
                  </v-checkbox>
                </td>
              </template>
              <template>
                <td>
                  <span>
                    {{ item.label }}
                  </span>
                </td>
                <td>
                  <span>
                    {{ item.description }}
                  </span>
                </td>
              </template>
            </tr>
          </template>
        </v-data-table>
      </v-card>
    </v-col>
    <v-dialog v-model="confirmationDialog" max-width="600" persistent>
      <application-admin-confirmation
        @setConfirmationDialog="setConfirmationDialog"
        @saveConfig="saveConfig"
        :changes="{ oldVal: excludedApplicationList, newVal: excludedApplicationListUpdated }"
        :dropdownList="applicationList"
        :dropdownMap="applicationListKeyedById"
      ></application-admin-confirmation>
    </v-dialog>
  </div>
</template>
<script>
import { mapActions, mapGetters, mapState } from "vuex";
import { cloneDeep, isArray, isEmpty, isEqual } from "lodash";

import ApplicationAdminChangeHistory from "./ApplicationAdminChangeHistory";
import ApplicationAdminConfirmation from "./ApplicationAdminConfirmation";
import GenericLoader from "@/components/GenericLoader";

export default {
  components: {
    ApplicationAdminChangeHistory,
    ApplicationAdminConfirmation,
    GenericLoader,
  },
  computed: {
    applicationList() {
      if (isEmpty(this.adminDropdownList)) {
        return;
      }

      const clonedApplicationList = cloneDeep(this.adminDropdownList);

      const applicationList = clonedApplicationList.filter(({ attributeName }) => {
        return attributeName === "applicationDropdownValueId";
      });

      const activeApplicationList = applicationList[0].valueList.filter(({ isDeprecated, label }) => {
        // Members should never be allowed to set Business Continuity Access (BCA) to Not impacted
        return !isDeprecated && label.toLowerCase() !== "business continuity access (bca)";
      });
      return activeApplicationList;
    },
    applicationListForHistory() {
      // Used by change history to display historical selections, including deprecated applications
      if (isEmpty(this.adminDropdownList)) {
        return;
      }

      const clonedApplicationList = cloneDeep(this.adminDropdownList);

      const applicationList = clonedApplicationList.filter(({ attributeName }) => {
        return attributeName === "applicationDropdownValueId";
      });

      // Include deprecated apps for historical display, but still exclude BCA
      const allApplicationList = applicationList[0].valueList.filter(({ label }) => {
        return label.toLowerCase() !== "business continuity access (bca)";
      });
      return allApplicationList;
    },
    applicationListKeyedById() {
      if (isEmpty(this.applicationList)) {
        return;
      }
      return this.applicationList.reduce((acc, item) => {
        acc[item.id] = item;
        return acc;
      }, {});
    },
    applicationListWithMemberSelectionsOriginal() {
      if (isEmpty(this.applicationList)) {
        return;
      }

      const enrichedList = this.applicationList.map(application => {
        if (this.excludedApplicationList.includes(application.id)) {
          application.memberSelection = true;
        } else {
          application.memberSelection = false;
        }
        return application;
      });

      return enrichedList;
    },
    applicationListWithMemberChoiceSelected() {
      if (this.showOptedOutList) {
        return this.applicationListWithMemberSelections.filter(({ memberSelection }) => memberSelection);
      }

      return this.applicationListWithMemberSelections;
    },
    excludedApplicationList() {
      return this.serviceArea.excludedApplicationList || [];
    },
    excludedApplicationListUpdated() {
      const applicationIdList = this.applicationListWithMemberSelections
        .map(({ id, memberSelection }) => {
          if (memberSelection) return id;
        })
        .filter(id => id);
      return applicationIdList;
    },
    filteredAuditLogList() {
      if (isEmpty(this.applicationAdminHistory)) {
        return [];
      }
      return this.applicationAdminHistory.filter(({ content }) => {
        // In create operations, the audit log records a single object, not an array of changes.
        if (!content || !isArray(content)) {
          return false;
        }
        return content.some(({ key }) => key === "excludedApplicationList");
      });
    },
    excludedApplicationListHasEdits() {
      return this.filteredAuditLogList.length > 0;
    },
    ...mapGetters({
      applicationAdminLastUpdatedDate: "auditLog/getApplicationAdminLastUpdatedDate",
      excludedApplicationListLastUpdate: "auditLog/getExcludedApplicationListLastUpdate",
    }),
    ...mapState({
      adminDropdownList: state => state.admin.dropdown.list,
      isLoadingDropdownList: state => state.admin.dropdown.isLoadingDropdownList,
      serviceAreaId: state => state.session.serviceArea,
      serviceArea: state => state.admin.serviceArea.record,
      applicationAdminHistory: state => state.auditLog.applicationAdminHistory,
    }),
  },
  async created() {
    try {
      await Promise.all([
        this.fetchAdminDropdownList(),
        this.getServiceArea(this.serviceAreaId),
        this.fetchApplicationAdminHistory(this.serviceAreaId),
      ]);
      this.applicationListWithMemberSelections = cloneDeep(this.applicationListWithMemberSelectionsOriginal);
    } catch (error) {
      console.error(error);
      this.$notifyError("Error fetching data");
    }
  },
  data() {
    return {
      applicationListWithMemberSelections: [],
      breadcrumbs: [
        { text: "Organization Settings", disabled: false, href: "organization-settings" },
        { text: "Application Adminstration", disabled: true, href: "application-administration" },
      ],
      confirmationDialog: false,
      enableEdit: false,
      headers: [
        {
          text: "Impacted",
          align: "left",
          sortable: true,
          value: "memberSelection",
        },
        {
          text: "Application",
          align: "left",
          value: "label",
        },
        {
          text: "Description",
          align: "left",
          value: "description",
        },
      ],
      isEmpty,
      isEqual,
      search: null,
      showHistory: false,
      showOptedOutList: false,
    };
  },
  methods: {
    ...mapActions({
      fetchApplicationAdminHistory: "auditLog/fetchApplicationAdminHistory",
      fetchAdminDropdownList: "admin/dropdown/list",
      getServiceArea: "admin/serviceArea/getServiceArea",
      patchUpdate: "admin/serviceArea/patchUpdate",
    }),
    async acknowledge() {
      const serviceAreaData = {
        serviceAreaId: this.serviceAreaId,
        data: { excludedApplicationList: [] },
      };
      this.enableEdit = false;
      try {
        await this.patchUpdate(serviceAreaData);
        this.$notifySuccess("Successfully updated");
        await this.refreshSAAuditLog();
        this.$router.push({ name: "adminOrganizationSettings" });
      } catch (e) {
        console.error(e);
        this.$notifyError("Error updating Service Area data");
      }
    },
    cancel() {
      this.enableEdit = false;
      this.applicationListWithMemberSelections = cloneDeep(this.applicationListWithMemberSelectionsOriginal);
    },
    getItemLabel(item) {
      return this.applicationListKeyedById[item].label;
    },
    async saveConfig() {
      const serviceAreaData = {
        serviceAreaId: this.serviceAreaId,
        data: { excludedApplicationList: this.excludedApplicationListUpdated },
      };
      this.enableEdit = false;
      try {
        await this.patchUpdate(serviceAreaData);
        this.$notifySuccess("Successfully updated");
      } catch (e) {
        console.error(e);
        this.$notifyError("Error updating Service Area data");
      }

      await this.refreshSAAuditLog();
    },
    async setConfirmationDialog(val) {
      this.confirmationDialog = val;
    },
    async refreshSAAuditLog() {
      try {
        await this.fetchApplicationAdminHistory(this.serviceAreaId);
      } catch (e) {
        console.error("Unable to refresh application admin audit history", e);
        this.$notifyError({ message: "Unable to refresh application admin audit history" });
      }
    },
    showSAHistory() {
      this.showHistory = true;
    },
  },
};
</script>

<style scoped>
.disabled-breadcrumb {
  color: black !important;
  cursor: default !important;
}
</style>
